import BaseClass from "./BaseClass";
import _ from "lodash";
import SharedApikeys from "./SharedApikeys";

export default class SharedCustomersServices extends BaseClass {
  constructor(data, skip_embedded = false) {
    super(data, skip_embedded);
    this.type || (this.type = "widget");
    this.details;
  }

  static get schema() {
    return {
      uuid: { editable: false },
      type: { editable: true }, // widget|script
      code: { editable: false },
      installed: { editable: true, type: Boolean },
      sharedAmocrm_uuid: { editable: true },
      sharedApikeys_uuid: { editable: true },
      sharedCustomers_uuid: { editable: true },
      sharedPeriods_uuid: { editable: true },
      sharedWidgets_uuid: { editable: true },
      sharedScripts_uuid: { editable: true },
      sharedIntegratin_uuid: { editable: true },
      created_at: { editable: false },
      updated_at: { editable: false }
    };
  }

  get actions() {
    return {
      edit: {
        handler: () => Promise.resolve("edit"),
        props: { type: "primary", size: "small" },
        in_edit_mode: false
      },
      close: {
        handler: () => Promise.resolve("close"),
        props: { type: "default", size: "small" },
        in_edit_mode: false
      },
      save: {
        handler: () => this._handleSave(),
        props: { type: "success", size: "small" },
        in_edit_mode: true
      },
      cancel: {
        handler: () => this._handleCancel(),
        props: { type: "primary", size: "small" },
        in_edit_mode: true
      }
    };
  }

  get isScript() {
    return this.type === "script";
  }

  get isWidget() {
    return this.type === "widget";
  }

  get hasValidIntegration() {
    try {
      return (
        this.sharedIntegrations_uuid === this._embedded.sharedIntegrations.uuid
      );
    } catch (e) {}
    return false;
  }

  get details() {
    let actualPeriod = this.actualPeriod;
    return {
      code: {
        value: this.code,
        status: this._getStatus(this.serviceCode)
      },
      customer: {
        value: this.customerName,
        status: this._getStatus(this.customerName)
      },
      amo_domain: {
        value: this.amoDomain,
        status: this._getStatus(this.amoDomain)
      },
      api_key: this.isScript
        ? undefined
        : {
            value: this.apiKey,
            status: this._getStatus(this.apiKey)
          },
      period: {
        value:
          actualPeriod || { ua: "нема", ru: "нету", en: "none" }[this._locale],
        extra: this._embedded ? this._embedded.sharedPeriods : {},
        status: this._getStatus(actualPeriod)
      },
      integration: {
        value: {
          true: { ua: "є", ru: "есть", en: "is there" },
          false: { ua: "нема", ru: "нету", en: "none" }
        }[this.hasIntegration][this._locale],
        status: this._getStatus(this.hasIntegration)
      }
    };
  }

  get image() {
    try {
      if (this.type === "script") {
        return require("../../assets/images/javascript-logo.png");
      }
      return (
        this._embedded.sharedWidgets.image ||
        require("../../assets/images/fail.svg")
      ); // crutch;
    } catch (e) {
      /**/
    }
    return require("../../assets/images/fail.svg");
  }

  get code() {
    try {
      return this._embedded[`shared${_.upperFirst(_.toLower(this.type))}s`]
        .code;
    } catch (e) {
      /**/
    }
    return undefined;
  }

  set code(v) {}

  get widgetVersion() {
    try {
      return this._embedded.sharedWidgets.version || "";
    } catch (e) {
      return "";
    }
  }

  get installedVersion() {
    try {
      return this._embedded.sharedWidgets.installed_version || "Not installed!";
    } catch (e) {
      return "Not installed!";
    }
  }

  get amoDomain() {
    try {
      return this._embedded.sharedAmocrm.domain || "";
    } catch (e) {
      return "";
    }
  }

  get serviceCode() {
    return this.code;
  }

  get customerName() {
    try {
      return this._embedded.sharedCustomers.name || "";
    } catch (e) {
      return "";
    }
  }

  get apiKey() {
    try {
      return this._embedded.sharedApikeys.key;
    } catch (e) {
      return undefined;
    }
  }

  get actualPeriod() {
    try {
      return this._embedded.sharedPeriods.actualPeriod.asString;
    } catch (e) {
      return "";
    }
  }

  get linkedCustomerName() {
    try {
      return this._embedded.sharedCustomers.name;
    } catch (e) {
      /**/
    }
    return undefined;
  }

  get linkedAmocrmDomain() {
    try {
      return this._embedded.sharedAmocrm.domain;
    } catch (e) {
      /**/
    }
    return undefined;
  }

  /**
   * @returns {boolean|*}
   * @deprecated
   */
  get canDelete() {
    try {
      return Object.values(this._embedded).reduce(
        (r, v) => r && v === null,
        true
      );
    } catch (e) {
      return true;
    }
  }

  get hasIntegration() {
    return (
      Boolean(this.sharedIntegrations_uuid) &&
      Boolean(this._embedded) &&
      Boolean(this._embedded.sharedIntegrations)
    );
  }

  static get canGroupBy() {
    return ["customerName", "amoDomain", "serviceCode"];
  }

  beShownForFilterString(filter, filters_array = []) {
    let result =
      !filter ||
      (this.amoDomain || "").toLowerCase().includes(filter.toLowerCase()) ||
      (this.code || "").toLowerCase().includes(filter.toLowerCase()) ||
      (this.customerName || "").toLowerCase().includes(filter.toLowerCase()) ||
      (this.apiKey || "").toLowerCase().includes(filter.toLowerCase());
    if (result && filters_array.length) {
      try {
        filters_array.forEach(v => {
          if (!this.beShownForFilterString(v)) {
            throw new Error("");
          }
        });
      } catch (e) {
        result = false;
      }
    }

    return result;
  }

  _handleCancel() {
    if (!this.uuid) {
      return Promise.resolve("cancel");
    }
    return this._api
      .query({
        query: require("../../graphql/services/services.graphql"),
        variables: this
      })
      .then(res => {
        Object.assign(this, res.data.shared_customers_services);
        return "cancel";
      });
  }

  _handleSave() {
    return this._api
      .mutate({
        mutation: require("../../graphql/services/services_update.graphql"),
        variables: this
      })
      .then(res => {
        // this.constructor(res.data.shared_customers_services);
        Object.assign(this, res.data.shared_customers_services);
        return "save";
      });
  }

  async _handleDelete(tryDeleteWidgetInAMO = false) {
    !tryDeleteWidgetInAMO || this.deleteWidgetInAMO();
    await Promise.all([
      this._embedded.sharedPeriods
        ? this._embedded.sharedPeriods._handleDelete()
        : "not deleted",
      this._embedded.sharedApikeys
        ? this._embedded.sharedApikeys._handleDelete()
        : "not deleted",
      this._embedded.sharedScripts
        ? this._embedded.sharedScripts.delete()
        : "not deleted"
    ]);

    return this._api
      .mutate({
        mutation: require("../../graphql/services/services_update.graphql"),
        variables: { uuid: this.uuid }
      })
      .then(res => {
        Object.assign(this, res.data.shared_customers_services);
        return "delete";
      });
  }

  _getStatus(v) {
    if (!v) {
      //  crutch for emit status TODO :: think about
      Object.assign(this, {
        get checkStatus() {
          return "error";
        }
      });
    } else if (this.checkStatus === undefined) {
      Object.assign(this, {
        get checkStatus() {
          return "ok";
        }
      });
    }
    return {
      icon: {
        class: v ? "el-icon-check" : "el-icon-close",
        style: {
          color: v ? "green" : "red"
        }
      }
    };
  }

  async _handleGenerateApikey() {
    if (this.details.api_key.value === undefined) {
      let apikey = await SharedApikeys._handleNewApikey(
        this.details.code === undefined ? "--UNKNOWN--" : "widget"
      );
      this.sharedApikeys_uuid = apikey.uuid;
      this._handleSave();
      return this;
    }
    this._embedded.sharedApikeys._handleRegenerate();
    return this;
  }

  async _handleDeleteApikey() {
    if (this.details.api_key.value === undefined) {
      return;
    }
    await this._embedded.sharedApikeys._handleDelete();
    this.sharedApikeys_uuid = this._embedded.sharedApikeys.uuid;
    this._handleSave();
    return this;
  }

  loadServiceInfoFromAmo() {
    if (
      !this.code ||
      !this.amoDomain ||
      !this.hasValidIntegration ||
      !this._embedded.sharedAmocrm ||
      !this._embedded.sharedAmocrm.password
    ) {
      return;
    }
    return this._api
      .query({
        query: require("../../graphql/opupet_query.graphql"),
        variables: {
          class: "\\App\\SPAModels\\Widgets\\WidgetActions",
          // method: "loadWidgetsFromAmo",
          method: "loadWidgetAsIntegrationFromAmo",
          params: {
            amocrm: this._embedded.sharedAmocrm,
            client_uuid: this._embedded.sharedIntegrations.integration_uuid
            // widget: this._embedded.sharedWidgets
          }
        }
      })
      .then(res => Object.values(res.data.opupet_query));
  }

  deleteWidgetInAMO() {
    if (!this._embedded.sharedAmocrm || !!this._embedded.sharedwWidgets) {
      return;
    }
    return this._api
      .query({
        query: require("../../graphql/opupet_query.graphql"),
        variables: {
          class: "\\App\\SPAModels\\Amocrm\\AnemoneConnector",
          method: "deleteWidgetInAmo",
          params: {
            amocrm: this._embedded.sharedAmocrm,
            widget: this._embedded.sharedWidgets
          }
        }
      })
      .then(res => res.data.opupet_query)
      .catch(() => false);
  }

  async makeIntegrationFromAccessCode(requestData) {
    let request = {
      uuid: this.uuid,
      requestData: Object.assign({}, requestData, {
        uuid: requestData.client_id,
        secret: requestData.client_secret,
        auth_code: requestData.code,
        _links: {
          redirect_uri: {
            href: requestData.redirect_uri
          }
        }
      })
    };
    let res = (
      await this._api.query({
        query: require("../../graphql/opupet_query.graphql"),
        variables: {
          class: "\\App\\JStorage\\SPA\\JSSharedCustomersServices",
          method: "makeIntegrationFromAccessCode",
          params: [request]
        }
      })
    ).data.opupet_query;
    Object.assign(this, new this.constructor(res));
    return res;
  }

  async checkOauth() {
    return (
      await this._api.query({
        query: require("../../graphql/opupet_query.graphql"),
        variables: {
          class: "\\App\\JStorage\\SPA\\JSSharedCustomersServices",
          method: "checkOAuth",
          params: [this.uuid]
        }
      })
    ).data.opupet_query;
  }
}
